import { useEffect, type RefObject } from 'react';
import { useKeyPress, useReactFlow, type Edge, type Node, type OnBeforeDelete } from '@xyflow/react';
import { useConfirmar } from '../design-system/Confirmacao';
import { useNotas } from './contexto-notas';
import type { DadosSeta } from './montar-nos-notas';

const APAGAR = ['Delete', 'Backspace'];

const setaDe = (e: Edge) => (e.data as Partial<DadosSeta> | undefined)?.setaUuid;
const uuidDaNota = (n: Node) => (n.type === 'nota' ? n.id.slice(2) : null);
const plural = (n: number, um: string, varios: string) => (n === 1 ? `1 ${um}` : `${n} ${varios}`);

/**
 * Delete (ou Backspace) apaga o que está selecionado, sempre com confirmação: notas e setas são excluídas; o cartão
 * de processo vai para a Lixeira pelo caminho de sempre (`aoCartao`, que pede o motivo). Grupos e módulos não saem.
 * O React Flow nunca remove nada sozinho: a gravação refaz a tela.
 */
export function useApagarSelecionados<N extends Node, E extends Edge>(aoCartao?: (processoUuid: string) => void) {
  const { excluirNota, excluirSeta } = useNotas((s) => s);
  const confirmar = useConfirmar();
  const antes: OnBeforeDelete<N, E> = async ({ nodes, edges }) => {
    const cartao = nodes.find((n) => n.type === 'cartao');
    if (cartao && aoCartao) {
      aoCartao(cartao.id.slice(2));
      return false;
    }
    const notas = nodes.map(uuidDaNota).filter((u): u is string => Boolean(u));
    const setas = edges.map(setaDe).filter((u): u is string => Boolean(u));
    if (notas.length + setas.length === 0) return false;
    const partes = [notas.length && plural(notas.length, 'nota', 'notas'), setas.length && plural(setas.length, 'seta', 'setas')].filter(Boolean).join(' e ');
    const sim = await confirmar({ titulo: 'Excluir do quadro?', mensagem: `Vai excluir ${partes}. Não dá para desfazer.`, rotulo: 'Excluir' });
    if (sim) {
      notas.forEach((uuid) => void excluirNota(uuid));
      setas.forEach((uuid) => void excluirSeta(uuid));
    }
    return false;
  };
  return { deleteKeyCode: APAGAR, onBeforeDelete: antes };
}

/**
 * Com Ctrl (ou Cmd) segurado, arrastar com o botão esquerdo move a tela, só enquanto a tecla está apertada: a
 * ferramenta escolhida não muda. O motor do quadro ignora cliques com Ctrl, então o arraste é feito aqui, na fase
 * de captura (antes de qualquer nó ou da seleção receber o clique).
 */
export function usePanComCtrl(alvo: RefObject<HTMLElement | null>) {
  const { getViewport, setViewport } = useReactFlow();
  const ctrl = useKeyPress(['Control', 'Meta']);
  useEffect(() => {
    const elemento = alvo.current;
    if (!elemento) return;
    const comecar = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      e.stopPropagation();
      const camera = getViewport();
      const arrastar = (m: PointerEvent) => setViewport({ x: camera.x + m.clientX - e.clientX, y: camera.y + m.clientY - e.clientY, zoom: camera.zoom });
      const soltar = () => {
        window.removeEventListener('pointermove', arrastar);
        window.removeEventListener('pointerup', soltar);
      };
      window.addEventListener('pointermove', arrastar);
      window.addEventListener('pointerup', soltar);
    };
    elemento.addEventListener('pointerdown', comecar, { capture: true });
    return () => elemento.removeEventListener('pointerdown', comecar, { capture: true });
  }, [alvo, getViewport, setViewport]);
  return ctrl;
}

/** Teclas dos quadros: `mao` (só a ferramenta Mover; Ctrl é tratado por usePanComCtrl) e as props da tecla Delete. */
export function useTeclasDoQuadro<N extends Node, E extends Edge>(ferramenta: string, aoCartao?: (processoUuid: string) => void) {
  return { mao: ferramenta === 'mao', apagarSetas: useApagarSelecionados<N, E>(aoCartao) };
}
